import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, X, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Tell me about the TPG Telecom case study",
  "How does Yuva scale UX teams?",
  "What's his experience with AI in design?",
];

const WELCOME =
  "Hi — I'm Yuva's AI concierge. Ask me anything about his experience, leadership, case studies, or skills.";

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(localStorage.getItem("yk-chat-session") || "");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current || null, message: msg }),
      });
      if (!res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let data;
          try { data = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (data.session_id) {
            sessionId.current = data.session_id;
            localStorage.setItem("yk-chat-session", data.session_id);
          }
          if (data.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
              return copy;
            });
          }
          if (data.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: data.error };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I couldn't reach the server. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Launcher */}
      <motion.button
        data-testid="chat-launcher"
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-6 right-24 z-[120] w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
        style={{ background: "var(--surface)", border: "1px solid var(--accent)", color: "var(--accent)" }}
        aria-label="Open AI concierge chat"
      >
        <motion.span animate={{ rotate: open ? 90 : 0 }} transition={{ type: "spring", stiffness: 200 }}>
          {open ? <X size={22} /> : <Sparkles size={22} />}
        </motion.span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 280, damping: 26 }}
            className="fixed bottom-24 right-6 z-[121] flex flex-col overflow-hidden surface-card"
            style={{
              width: "min(390px, calc(100vw - 3rem))",
              height: "min(560px, calc(100vh - 8rem))",
              background: "var(--surface)",
            }}
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b hairline">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-9 h-9 rounded-full" style={{ background: "var(--accent-soft)", color: "var(--accent)" }}>
                  <Sparkles size={16} />
                </span>
                <div>
                  <p className="text-sm font-medium" style={{ color: "var(--text)" }}>AI Concierge</p>
                  <p className="text-[0.65rem] uppercase tracking-widest" style={{ color: "var(--muted-t)" }}>Powered by Claude</p>
                </div>
              </div>
              <button data-testid="chat-close" onClick={() => setOpen(false)} aria-label="Close chat" className="opacity-60 hover:opacity-100 transition-opacity">
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} data-testid={`chat-message-${m.role}`} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                  <div
                    className="max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap"
                    style={
                      m.role === "user"
                        ? { background: "var(--accent)", color: "var(--bg)", borderRadius: "16px 16px 4px 16px" }
                        : { background: "var(--surface-2)", color: "var(--text)", borderRadius: "16px 16px 16px 4px", border: "1px solid var(--line)" }
                    }
                  >
                    {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>

            {/* Suggestions (only before first user message) */}
            {messages.filter((m) => m.role === "user").length === 0 && (
              <div className="px-5 pb-3 flex flex-wrap gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`chat-suggestion-${i}`}
                    onClick={() => send(s)}
                    className="text-xs px-3 py-2 rounded-full border transition-colors hover:opacity-80"
                    style={{ borderColor: "var(--line)", color: "var(--muted-t)" }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="p-3 border-t hairline flex items-end gap-2">
              <textarea
                ref={inputRef}
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder="Ask about Yuva's work…"
                className="flex-1 resize-none bg-transparent outline-none text-sm py-2 px-2 max-h-24"
                style={{ color: "var(--text)" }}
              />
              <button
                data-testid="chat-send"
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-opacity disabled:opacity-40"
                style={{ background: "var(--accent)", color: "var(--bg)" }}
                aria-label="Send message"
              >
                {streaming ? <ArrowUpRight size={16} className="animate-pulse" /> : <Send size={16} />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
